import { useQuery } from "@apollo/client";
import { Form, Formik, FormikHelpers } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useMemo } from "react";

import {
  GetLaneUsageQuery,
  GetRateLimitsQuery,
  ProviderRateLimitKey,
  UpdateLaneRateLimitsMutation,
  UpdateLaneRateLimitsMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  GET_LANE_USAGE,
  GET_RATE_LIMITS,
  UPDATE_LANE_RATE_LIMITS,
} from "~frontend/graphql/rateLimits";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import ProviderLimitSection from "./ProviderLimitSection";
import { RateLimitFormValues } from "./types";

function formikInitialState(
  queryResult: GetRateLimitsQuery
): RateLimitFormValues {
  return Object.fromEntries(
    queryResult.listLaneRateLimits.map(laneLimit => [
      `${laneLimit.providerRateLimitKey}|${laneLimit.lane}`,
      String(laneLimit.tokenRatePerSecond),
    ])
  );
}

const providerSections: {
  [key in ProviderRateLimitKey]: {
    title: string;
    order: number;
    unit: "segments" | "messages";
  };
} = {
  [ProviderRateLimitKey.BandwidthTenDlcSms]: {
    title: "10DLC - SMS",
    unit: "segments",
    order: 1,
  },
  [ProviderRateLimitKey.BandwidthTenDlcMms]: {
    title: "10DLC - MMS",
    unit: "messages",
    order: 2,
  },
  [ProviderRateLimitKey.BandwidthTollFreeSms]: {
    title: "Tollfree - SMS",
    unit: "segments",
    order: 3,
  },
  [ProviderRateLimitKey.BandwidthTollFreeMms]: {
    title: "Tollfree - MMS",
    unit: "messages",
    order: 4,
  },
  [ProviderRateLimitKey.BandwidthShortCodeSms]: {
    title: "Short Code - SMS",
    unit: "segments",
    order: 6,
  },
  [ProviderRateLimitKey.BandwidthShortCodeMms]: {
    title: "Short Code - MMS",
    unit: "messages",
    order: 5,
  },
  [ProviderRateLimitKey.BandwidthUnregisteredSms]: {
    title: "Unregistered - SMS",
    unit: "segments",
    order: 7,
  },
  [ProviderRateLimitKey.BandwidthUnregisteredMms]: {
    title: "Unregistered - MMS",
    unit: "messages",
    order: 8,
  },
  [ProviderRateLimitKey.SinchTenDlcSms]: {
    title: "Sinch 10DLC - SMS",
    unit: "segments",
    order: 9,
  },
  [ProviderRateLimitKey.SinchTenDlcMms]: {
    title: "Sinch 10DLC - MMS",
    unit: "messages",
    order: 10,
  },
  [ProviderRateLimitKey.FakeDefault]: {
    title: "Fake Provider (Internal Testing)",
    order: 11,
    unit: "segments",
  },
};

export default function RateLimits(): ReactElement {
  useBreadcrumbs([
    {
      label: "Rate Limits",
      path: "/staff/rate-limits",
    },
  ]);

  const rateLimitsQuery = useQuery<GetRateLimitsQuery>(GET_RATE_LIMITS);

  const usageQuery = useQuery<GetLaneUsageQuery>(GET_LANE_USAGE, {
    pollInterval: 15000,
  });

  const [updateLaneRateLimits] = useMutation<
    UpdateLaneRateLimitsMutation,
    UpdateLaneRateLimitsMutationVariables
  >(UPDATE_LANE_RATE_LIMITS);

  const getErrorMessage = useErrorMessageFormatter();
  const snackbar = useSnackbar();

  const usageByProviderKey = useMemo(
    () =>
      _.groupBy(
        usageQuery.data?.getLaneUsage ?? [],
        u => u.providerRateLimitKey
      ),
    [usageQuery.data?.getLaneUsage]
  );

  const lanes = useMemo(
    () =>
      Array.from(
        new Set(rateLimitsQuery.data?.listLaneRateLimits.map(u => u.lane) ?? [])
      ).sort(),
    [rateLimitsQuery.data?.listLaneRateLimits]
  );

  if (rateLimitsQuery.loading || usageQuery.loading) {
    return <Loading />;
  }

  if (rateLimitsQuery.error || usageQuery.error) {
    return <ErrorCard errors={[rateLimitsQuery.error || usageQuery.error]} />;
  }

  async function handleSubmit(
    vals: RateLimitFormValues,
    formik: FormikHelpers<RateLimitFormValues>
  ) {
    const laneRateLimits = Object.entries(vals).map(
      ([providerAndLane, tokenRatePerSecondStr]) => {
        const [providerRateLimitKey, lane] = providerAndLane.split("|");
        return {
          providerRateLimitKey: providerRateLimitKey as ProviderRateLimitKey,
          lane,
          tokenRatePerSecond: Number(tokenRatePerSecondStr),
        };
      }
    );

    const { errors } = await updateLaneRateLimits({
      variables: {
        input: {
          laneRateLimits,
        },
      },
    });

    if (errors) {
      formik.setErrors({
        [submitErrorField]: getErrorMessage(errors),
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    } else {
      const newRateLimits = await rateLimitsQuery.refetch();

      if (newRateLimits.data) {
        formik.resetForm({
          values: formikInitialState(newRateLimits.data),
        });
        snackbar.enqueueSnackbar("Rate limits updated", {
          variant: "success",
        });
      }
    }
  }

  return (
    <PageContainer>
      <Formik
        onSubmit={handleSubmit}
        initialValues={formikInitialState(rateLimitsQuery.data!)}
      >
        <Form>
          <FormikErrorBanner />
          {_.sortBy(
            rateLimitsQuery.data!.listProviderRateLimits,
            limit => providerSections[limit.providerRateLimitKey].order
          ).map(providerLimit => (
            <ProviderLimitSection
              key={providerLimit.providerRateLimitKey}
              providerRateLimitKey={providerLimit.providerRateLimitKey}
              label={providerSections[providerLimit.providerRateLimitKey].title}
              unit={providerSections[providerLimit.providerRateLimitKey].unit}
              providerLimit={providerLimit.tokenRatePerSecond}
              lanes={lanes}
              usage={usageByProviderKey[providerLimit.providerRateLimitKey]}
            />
          ))}

          <FlexBox sx={{ justifyContent: "end", mt: 4 }}>
            <FormikSubmitButton
              submitText="Update Rate Limits"
              loadingText="Saving..."
            />
          </FlexBox>
        </Form>
      </Formik>
    </PageContainer>
  );
}
